import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TrendingUp, DollarSign, Percent, Users, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ProfitabilityPage = () => {
  const [reportType, setReportType] = useState('customer');
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [productData, setProductData] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;


  const fetchCustomerProfitability = useCallback(async () => {
    const response = await fetch(`${backendUrl}/api/profitability/customer-profitability`);
    const data = await response.json();
    setCustomerData(data);
  }, [backendUrl]);

  const fetchProductRevenue = useCallback(async () => {
    const response = await fetch(`${backendUrl}/api/profitability/product-revenue-analysis`);
    const data = await response.json();
    setProductData(data);
  }, [backendUrl]);

  const loadReportData = useCallback(async () => {
    setLoading(true);
    try {
      if (reportType === 'customer') {
        await fetchCustomerProfitability();
      } else {
        await fetchProductRevenue();
      }
    } catch (error) {
      toast.error('Failed to load profitability data');
    } finally {
      setLoading(false);
    }
  }, [reportType, fetchCustomerProfitability, fetchProductRevenue]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Profitability Analysis</h1>
            <p className="text-gray-600">Customer and product profitability insights</p>
          </div>
          <Button onClick={loadReportData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Report Type Selector */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="w-64">
            <label className="block text-sm font-medium mb-2">Analysis Type</label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer Profitability</SelectItem>
                <SelectItem value="product">Product Revenue Analysis</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Customer Profitability */}
        {reportType === 'customer' && customerData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Users className="w-4 h-4 mr-2 text-orange-600" />
                    Customers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{customerData.total_customers_analyzed}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center">
                    <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                    Total Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(customerData.total_revenue)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2 text-orange-600" />
                    Total Profit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(customerData.total_profit)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Percent className="w-4 h-4 mr-2 text-orange-600" />
                    Avg Margin
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{customerData.average_margin.toFixed(1)}%</div>
                </CardContent>
              </Card>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold">Customer Profitability Analysis</h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer ID</TableHead>
                    <TableHead>Customer Name</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Gross Profit</TableHead>
                    <TableHead className="text-right">Margin %</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerData.customers.map((customer) => (
                    <TableRow key={customer.customer_id}>
                      <TableCell className="font-medium">{customer.customer_id}</TableCell>
                      <TableCell>{customer.customer_name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(customer.revenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(customer.cost)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(customer.gross_profit)}</TableCell>
                      <TableCell className="text-right">
                        <span className={customer.profit_margin > 30 ? 'text-green-600 font-semibold' : customer.profit_margin > 20 ? 'text-orange-600' : 'text-gray-600'}>
                          {customer.profit_margin.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{customer.orders}</TableCell>
                      <TableCell className="text-right">{formatCurrency(customer.outstanding)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* Product Revenue Analysis */}
        {reportType === 'product' && productData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Total Products</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{productData.total_products}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(productData.total_revenue)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Total Quantity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{productData.total_quantity.toFixed(0)}</div>
                </CardContent>
              </Card>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold">Product Revenue Analysis</h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material ID</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Avg Price</TableHead>
                    <TableHead className="text-right">Price Variance</TableHead>
                    <TableHead className="text-right">Profit Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productData.products.map((product) => (
                    <TableRow key={product.material_id}>
                      <TableCell className="font-medium">{product.material_id}</TableCell>
                      <TableCell className="max-w-xs truncate">{product.description}</TableCell>
                      <TableCell className="text-right">{product.quantity.toFixed(0)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(product.revenue)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(product.avg_price)}</TableCell>
                      <TableCell className="text-right">{product.price_variance.toFixed(1)}%</TableCell>
                      <TableCell className="text-right">
                        <span className={product.profit_margin > 30 ? 'text-green-600 font-semibold' : 'text-gray-600'}>
                          {product.profit_margin.toFixed(1)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default ProfitabilityPage;